import { maxBy } from "lib/actions/util";
import { promoteVersion } from "lib/actions/assets/promoteVersion";
import { revertToVersion } from "lib/actions/assets/revertToVersion";
import { popToastInfo, popToastSuccess } from "lib/actions/toast";
import { useMemo, useState } from "react";
import { useAsset } from "./useAsset";
import { AssetVersionPrefix } from "lib/types/assets/AssetVersionPrefix";
import { AssetVersionModel } from "lib/types/assets/AssetVersionModel";

let promotingDoneOn = null;
let revertingDoneOn = null;

export const useAssetVersion = (assetId: number, versionId: number) => {
  const { asset, isLoading, hasError, mutate } = useAsset(assetId);
  const [isPromoting, setPromoting] = useState(false);
  const [isPromotingDone, setPromotingDone] = useState(false);
  const [isReverting, setReverting] = useState(false);
  const [isRevertingDone, setRevertingDone] = useState(false);

  const version = useMemo(
    () =>
      asset && {
        ...asset.versions.find((v) => v.id === versionId),
        asset,
      },
    [asset, versionId],
  );
  const promotion = getPromotion(version);
  const mostRecentVersion =
    asset && maxBy(asset.versions, (version) => version.createdOn);
  const canRevert =
    mostRecentVersion && version && mostRecentVersion.id !== version.id;

  const markPromotingDone = () => {
    setPromotingDone(true);
    const doneOn = (promotingDoneOn = new Date().getTime());
    setTimeout(
      () => doneOn === promotingDoneOn && setPromotingDone(false),
      5000,
    );
  };

  const markRevertingDone = () => {
    setRevertingDone(true);
    const doneOn = (revertingDoneOn = new Date().getTime());
    setTimeout(
      () => doneOn === revertingDoneOn && setRevertingDone(false),
      5000,
    );
    popToastSuccess("This is now the latest version!");
  };

  const promote = async () => {
    setPromoting(true);
    try {
      await promoteVersion(versionId, assetId);
      await mutate();
      markPromotingDone();
    } finally {
      setPromoting(false);
    }
  };

  const revertTo = async () => {
    setReverting(true);
    popToastInfo("Setting as latest version...");
    try {
      await revertToVersion(versionId, assetId);
      await mutate();
      markRevertingDone();
    } finally {
      setReverting(false);
    }
  };

  return {
    version,
    promotion,
    isPromoting,
    isPromotingDone,
    canRevert,
    isReverting,
    isRevertingDone,
    promote,
    revertTo,
    hasError: !assetId || !versionId || hasError,
    isLoading: !hasError && isLoading,
  };
};

const getPromotion = (
  version: AssetVersionModel,
): {
  statusLabel: string;
  actionLabel: string;
  prefix: AssetVersionPrefix;
} => {
  if (!version || !version.canPromote) return null;
  if (version.prefix === AssetVersionPrefix.preliminary && version.minor)
    return {
      statusLabel: "Shared for coordination",
      actionLabel: "Share for coordination",
      prefix: AssetVersionPrefix.preliminary,
    };
  if (version.prefix === AssetVersionPrefix.preliminary && !version.minor)
    return {
      statusLabel: "Published for construction",
      actionLabel: "Publish for construction",
      prefix: AssetVersionPrefix.contractual,
    };
  if (version.prefix === AssetVersionPrefix.contractual)
    return {
      statusLabel: "Archived as built",
      actionLabel: "Archive as built",
      prefix: AssetVersionPrefix.archive,
    };
  return null;
};
